import Spinner from '../../common/Spinner';
import Row from './Row';
import {
  AlertEditableFieldProps,
  AlertSettingsResponseProps
} from './types';
import {ProcessTypeLabelsMap, AlertType} from './constants';
import {Dispatch, FC, SetStateAction} from 'react';
import {
  getAllAlertSettings,
  updateAlertSetting
} from '../../../api/settings/alert_alarm';
import {
  secondsInAMinute,
  thresholdToPrependZero,
  maxTaskTimeInSeconds
} from './constants';
import {convertTimeInSeconds} from '../target_task_time/helpers';
import TableHeaderRow from '../../common/TableHeaderRow';
import {useTranslation} from 'react-i18next';
import {useSnackbarHook} from '../../../hooks/useSnackbar';

interface AlertListProps {
  data: Nullable<AlertSettingsResponseProps>,
  setData:
    Dispatch<AlertSettingsResponseProps> |
    Dispatch<SetStateAction<null>>,
  alertType: string,
  isSwitching: boolean,
  isRelative: boolean,
}

const AlertList: FC<AlertListProps> = ({
  data,
  setData,
  alertType,
  isSwitching,
  isRelative
}) => {
  const {t} = useTranslation('alertAlarm');
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const entity = {
    fields: [{
      name: 'processType',
      label: 'Process',
      permission: null
    },
    {
      name: 'range',
      label: isRelative ? 'Alert range, %' : 'Alert range, min : sec',
      permission: null
    }
    ]
  };

  const handleUpdate = async (
      values: AlertEditableFieldProps,
      processType: string
  ) => {
    let range = isRelative ?
      Number(values?.range) || 1 :
      convertTimeInSeconds({minutes: values.minutes, seconds: values.seconds});

    range = !isRelative && range > maxTaskTimeInSeconds ?
    maxTaskTimeInSeconds : range;

    try {
      await updateAlertSetting(alertType, processType, range);
      const alertTypeToast = AlertType[alertType];
      const process = ProcessTypeLabelsMap[processType];
      addSuccessToast(t('toasts.updateAlertRangeMessage', {alertTypeToast, process}));
      const response = await getAllAlertSettings(alertType);
      setData(response);
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  const renderRows = () => {
    if (!data) return null;

    return data.map(({
      processType,
      relativeRange,
      absoluteRange
    }, index) => {
      const absMinutes = `${Math.floor(absoluteRange / secondsInAMinute)}`;
      let absSeconds = `${Math.floor(absoluteRange % secondsInAMinute)}`;
      absSeconds = Number(absSeconds) > thresholdToPrependZero ?
        absSeconds : `0${absSeconds}`;

      const editableCellItem = {
        rowId: index + 1,
        cellData: isRelative ?
          {range: `${relativeRange}`} :
          {minutes: absMinutes, seconds: absSeconds},
        editHandler: (id, value) => handleUpdate(value, processType),
        permission: null
      };

      return <Row
        key={`alert-settings-row-${processType}`}
        processType={ProcessTypeLabelsMap?.[processType]}
        cellItem={editableCellItem}
        isRelative={isRelative}
        isTimeInput
      />;
    });
  };

  return (
    <Spinner
      isLoading={isSwitching}
      loadingText={
        `Switching to ${isRelative ? 'relative' : 'absolute'} range`
      }>
      <table className="table">
        <thead>
          <TableHeaderRow
            tableData={entity.fields}
          />
        </thead>
        <tbody>
          {renderRows()}
        </tbody>
      </table>
    </Spinner>

  );
};

export default AlertList;
